/* Hush — Platform detection & native plugin shims.
   On the web build, isNative()=false and native plugin imports gracefully no-op
   so the same React code runs in browser, Capacitor iOS, and Capacitor Android. */

let capacitorCore = null;
try {
  // dynamic require so the web build doesn't fail when @capacitor/core isn't installed
  // eslint-disable-next-line global-require
  capacitorCore = require("@capacitor/core");
} catch (_) {
  capacitorCore = null;
}

export function isNative() {
  try {
    return Boolean(capacitorCore && capacitorCore.Capacitor && capacitorCore.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

export function platform() {
  try {
    return capacitorCore?.Capacitor?.getPlatform?.() || "web";
  } catch {
    return "web";
  }
}

/* === Apple Sign In wrapper ===
   On iOS native, uses @capacitor-community/apple-sign-in.
   On web, uses Apple JS SDK (loaded from https://appleid.cdn-apple.com).
*/
export async function signInWithApple() {
  if (isNative() && platform() === "ios") {
    try {
      // eslint-disable-next-line global-require
      const { SignInWithApple } = require("@capacitor-community/apple-sign-in");
      const res = await SignInWithApple.authorize({
        clientId: "app.hush.journal", // bundle id
        redirectURI: "https://hush.app/auth-callback",
        scopes: "email name",
      });
      const r = res?.response || {};
      return {
        identity_token: r.identityToken,
        user: r.givenName || r.familyName ? { firstName: r.givenName, lastName: r.familyName } : null,
      };
    } catch (e) {
      throw new Error(e?.message || "Apple sign-in failed");
    }
  }
  // Web fallback — needs Apple Services ID configured.
  // For now we throw so the UI can hide the button gracefully on web.
  throw new Error("Apple sign-in is currently available only inside the iOS app.");
}

/* === Google Sign In wrapper (Android native) ===
   iOS uses Apple Sign-In, web uses the existing redirect flow.
   On Android Capacitor: opens Emergent's hosted Google auth in an in-app
   browser (Custom Tab), listens for the deep-link callback
   `app.hush.journal://auth-callback#session_id=...`, then exchanges the
   session_id for a real session via POST /api/auth/google.

   Requires:
   - AndroidManifest.xml has an intent-filter for the `app.hush.journal://`
     scheme on the MainActivity.
   - A small bridge page at https://apphush.online/auth/callback that reads
     `window.location.hash` and immediately redirects to the deep-link URL.
*/
export async function signInWithGoogleAndroid(api) {
  if (!(isNative() && platform() === "android")) {
    throw new Error("This function is only for Android native.");
  }
  // eslint-disable-next-line global-require
  const { Browser } = require("@capacitor/browser");
  // eslint-disable-next-line global-require
  const { App } = require("@capacitor/app");

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // For Android Capacitor, we point Emergent at the production /auth/callback
  // page which then bounces to our app's deep link. We must use the apphush.online
  // domain (not localhost) because Emergent only redirects to https origins.
  const bridgeUrl = "https://apphush.online/auth/callback";
  const url = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(bridgeUrl)}`;

  // Open the auth flow in the in-app browser (Chrome Custom Tab on Android).
  await Browser.open({ url, presentationStyle: "popover" });

  // Wait for the deep link callback. App.addListener('appUrlOpen') fires when
  // the OS routes app.hush.journal:// back to our app.
  return new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = (handle) => { try { handle.remove(); } catch (_) { /* noop */ } };

    const handle = App.addListener("appUrlOpen", async (data) => {
      const incomingUrl = data?.url || "";
      if (!incomingUrl.includes("auth-callback")) return;
      if (settled) return;
      settled = true;
      cleanup(handle);
      try { await Browser.close(); } catch (_) { /* noop */ }

      // Parse session_id from the URL hash, e.g.
      // app.hush.journal://auth-callback#session_id=abc123
      const m = incomingUrl.match(/session_id=([^&]+)/);
      if (!m) {
        reject(new Error("No session_id in callback URL"));
        return;
      }
      const session_id = decodeURIComponent(m[1]);
      try {
        const { data: user } = await api.post("/auth/google", { session_id });
        resolve(user);
      } catch (e) {
        reject(e);
      }
    });

    // Safety: if user dismisses the browser without completing auth, reject
    // after a short delay so the spinner doesn't hang forever.
    const browserCloseHandle = Browser.addListener("browserFinished", () => {
      setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup(handle);
        cleanup(browserCloseHandle);
        reject(new Error("CANCELLED"));
      }, 500);
    });
  });
}

/* === Native Push wrapper ===
   On iOS/Android, registers with APNs/FCM and POSTs the device token
   to /api/push/subscribe with keys.auth = 'native'. */
export async function registerNativePush(api) {
  if (!isNative()) return false;
  try {
    // eslint-disable-next-line global-require
    const { PushNotifications } = require("@capacitor/push-notifications");
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return false;
    await PushNotifications.register();
    return new Promise((resolve) => {
      PushNotifications.addListener("registration", async (token) => {
        try {
          await api.post("/push/subscribe", {
            endpoint: `native:${platform()}:${token.value}`,
            keys: { p256dh: "native", auth: "native" },
          });
          resolve(true);
        } catch {
          resolve(false);
        }
      });
      PushNotifications.addListener("registrationError", () => resolve(false));
    });
  } catch (e) {
    return false;
  }
}
